/**
 * DriverSafetyPage · v2.7.33 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Fleet → Driver Safety terminal.
 *
 *   • Top: grid of all drivers with their grounded/active status
 *   • Middle: ground / unground modal with reason matrix dropdown
 *   • Bottom: read-only audit log table
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, ShieldAlert, RefreshCw, Lock, Unlock, ScrollText, Search } from "lucide-react";
import adminApi from "@/lib/adminApi";

export default function DriverSafetyPage() {
  const [drivers, setDrivers] = useState([]);
  const [log, setLog] = useState([]);
  const [reasons, setReasons] = useState({ ground: {}, unground: {} });
  const [search, setSearch] = useState("");
  const [busyReload, setBusyReload] = useState(false);
  const [modal, setModal] = useState(null); // { driver, mode: "ground"|"unground" }
  const [reasonKey, setReasonKey] = useState("");
  const [reasonNotes, setReasonNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(async () => {
    setBusyReload(true);
    try {
      const [a, b, c] = await Promise.all([
        adminApi.get("/admin/fleet/safety/drivers"),
        adminApi.get("/admin/fleet/safety/log?limit=200"),
        adminApi.get("/admin/fleet/safety/reasons"),
      ]);
      setDrivers(a.data || []);
      setLog(b.data || []);
      setReasons(c.data || { ground: {}, unground: {} });
    } catch (e) {
      toast.error(`Driver Safety load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyReload(false);
    }
  }, []);
  useEffect(() => { reload(); const id = setInterval(reload, 30000); return () => clearInterval(id); }, [reload]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((d) =>
      (d.name || "").toLowerCase().includes(q)
      || (d.email || "").toLowerCase().includes(q)
      || (d.phone || "").includes(q)
      || (d.id || "").includes(q));
  }, [drivers, search]);

  const groundedCount = drivers.filter((d) => d.safety_grounded).length;

  const openModal = (driver, mode) => {
    setModal({ driver, mode });
    setReasonKey("");
    setReasonNotes("");
  };
  const closeModal = () => { setModal(null); setReasonKey(""); setReasonNotes(""); };
  const submit = async () => {
    if (!modal) return;
    if (!reasonKey) { toast.error("Select a reason"); return; }
    if (reasonKey === "other" && !reasonNotes.trim()) {
      toast.error("Notes required when reason is 'Other'");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = `/admin/fleet/safety/${modal.driver.id}/${modal.mode}`;
      await adminApi.post(endpoint, { reason: reasonKey, notes: reasonNotes.trim() || null });
      toast.success(`${modal.mode === "ground" ? "Grounded" : "Restored"} ${modal.driver.name || modal.driver.id.slice(0, 8)}`);
      closeModal();
      reload();
    } catch (e) {
      toast.error(`${modal.mode}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const reasonMatrix = modal?.mode === "ground" ? reasons.ground : reasons.unground;

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid="driver-safety-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider" data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4"/> Admin Console
            </Link>
            <div className="h-6 w-px bg-zinc-800"/>
            <ShieldCheck className="h-6 w-6 text-red-300"/>
            <h1 className="text-2xl font-extrabold tracking-tight">Driver Safety</h1>
            <Badge className="bg-zinc-900 border-zinc-700 text-zinc-300 text-[10px] uppercase tracking-widest">Fleet · Compliance</Badge>
          </div>
          <Button onClick={reload} disabled={busyReload} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2"/>Refresh
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="bg-zinc-900 border-zinc-800 p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Total drivers</div>
            <div className="text-4xl font-extrabold tabular-nums mt-1 text-zinc-100" data-testid="total-drivers-count">{drivers.length}</div>
          </Card>
          <Card className="bg-rose-500/10 border-rose-400/40 p-4">
            <div className="text-[10px] uppercase tracking-widest text-rose-300 font-bold">Currently grounded</div>
            <div className="text-4xl font-extrabold tabular-nums mt-1 text-rose-100" data-testid="grounded-count">{groundedCount}</div>
          </Card>
          <Card className="bg-emerald-500/10 border-emerald-400/40 p-4">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Active &amp; eligible</div>
            <div className="text-4xl font-extrabold tabular-nums mt-1 text-emerald-100" data-testid="active-count">{drivers.length - groundedCount}</div>
          </Card>
        </div>

        {/* Driver grid */}
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-2">
              <ShieldAlert className="h-3.5 w-3.5 text-red-300"/> Driver Status · Grounded First
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500"/>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / email / phone" className="h-8 pl-7 w-64 text-xs bg-zinc-950 border-zinc-800" data-testid="driver-search"/>
            </div>
          </div>
          {filtered.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">No drivers match.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((d) => {
              const g = d.safety_grounded;
              return (
                <div
                  key={d.id}
                  className={`rounded-lg p-3.5 border-l-4 ${g ? "border-l-red-500 border border-red-500/40 bg-rose-950/30" : "border-l-emerald-500 border border-zinc-800 bg-zinc-950/60"}`}
                  data-testid={`driver-row-${d.id?.slice(-8)}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-sm font-extrabold text-zinc-100 truncate">{d.name || d.id?.slice(0, 8)}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{d.email || "—"} · {d.phone || "no phone"}</div>
                    </div>
                    {g
                      ? <Badge className="bg-red-500/25 text-red-100 border-red-400/70 font-extrabold uppercase tracking-wider text-[10px]">Grounded</Badge>
                      : <Badge className="bg-emerald-500/25 text-emerald-100 border-emerald-400/70 font-extrabold uppercase tracking-wider text-[10px]">Active</Badge>}
                  </div>
                  {g && (
                    <div className="text-[11px] text-rose-200 mb-2 line-clamp-2">
                      <span className="font-bold">{d.safety_reason_label || d.safety_reason}</span>
                      {d.safety_notes ? <span className="opacity-80"> · {d.safety_notes}</span> : null}
                    </div>
                  )}
                  {d.safety_changed_at && (
                    <div className="text-[9px] text-zinc-500 mb-2">Last action: {new Date(d.safety_changed_at).toLocaleString()} · by {d.safety_changed_by || "—"}</div>
                  )}
                  <div className="flex gap-2">
                    {g ? (
                      <Button size="sm" onClick={() => openModal(d, "unground")} className="flex-1 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 border border-emerald-400/50 h-8 text-xs font-bold uppercase tracking-wider" data-testid={`unground-${d.id?.slice(-8)}`}>
                        <Unlock className="h-3.5 w-3.5 mr-1.5"/> Unground
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => openModal(d, "ground")} className="flex-1 bg-red-500/15 text-red-200 hover:bg-red-500/25 border border-red-400/50 h-8 text-xs font-bold uppercase tracking-wider" data-testid={`ground-${d.id?.slice(-8)}`}>
                        <Lock className="h-3.5 w-3.5 mr-1.5"/> Ground
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Audit log */}
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-amber-400/40 flex items-center gap-2">
            <ScrollText className="h-3.5 w-3.5 text-amber-300"/> Compliance Audit Log
            <Badge className="bg-zinc-950 border-zinc-700 text-zinc-300 ml-auto">{log.length} entr{log.length === 1 ? "y" : "ies"}</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 pr-3">Timestamp</th>
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Driver</th>
                  <th className="py-2 pr-3">Operator</th>
                  <th className="py-2 pr-3">Reason</th>
                  <th className="py-2 pr-3">Notes</th>
                </tr>
              </thead>
              <tbody data-testid="audit-log-body">
                {log.length === 0 && (
                  <tr><td colSpan={6} className="py-4 text-center text-zinc-500">No audit entries yet.</td></tr>
                )}
                {log.map((r, i) => {
                  const driver = drivers.find((d) => d.id === r.driver_id);
                  return (
                    <tr key={r.id || r.audit_id || `${r.driver_id}-${r.ts}-${i}`} className="border-b border-zinc-800/60">
                      <td className="py-1.5 pr-3 text-zinc-400 tabular-nums">{new Date(r.ts).toLocaleString()}</td>
                      <td className="py-1.5 pr-3">
                        {r.action === "ground"
                          ? <Badge className="bg-red-500/20 text-red-200 border-red-400/50 text-[10px] font-extrabold uppercase">Ground</Badge>
                          : <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/50 text-[10px] font-extrabold uppercase">Unground</Badge>}
                      </td>
                      <td className="py-1.5 pr-3 text-zinc-200">{driver?.name || r.driver_id?.slice(0, 8) || "—"}</td>
                      <td className="py-1.5 pr-3 text-zinc-400">{r.operator_name || r.operator_id || "—"}</td>
                      <td className="py-1.5 pr-3 text-zinc-200">{r.reason_label || r.reason_key}</td>
                      <td className="py-1.5 pr-3 text-zinc-400 max-w-md truncate">{r.notes || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Ground / Unground modal */}
      <Dialog open={!!modal} onOpenChange={(v) => !v && closeModal()}>
        <DialogContent className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="safety-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {modal?.mode === "ground"
                ? <><Lock className="h-4 w-4 text-red-300"/> Ground Driver</>
                : <><Unlock className="h-4 w-4 text-emerald-300"/> Restore Driver</>}
              <span className="text-zinc-400 font-normal text-sm">· {modal?.driver?.name || modal?.driver?.id?.slice(0, 8)}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Reason</div>
              <Select value={reasonKey} onValueChange={setReasonKey}>
                <SelectTrigger className="bg-zinc-900 border-zinc-700" data-testid="reason-select">
                  <SelectValue placeholder="Select a reason…"/>
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700">
                  {Object.entries(reasonMatrix || {}).map(([key, label]) => (
                    <SelectItem key={key} value={key} className="text-zinc-100 focus:bg-amber-400/10" data-testid={`reason-opt-${key}`}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(reasonKey === "other" || reasonKey) && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">
                  Notes {reasonKey === "other" && <span className="text-red-300">· required</span>}
                </div>
                <Textarea value={reasonNotes} onChange={(e) => setReasonNotes(e.target.value)} placeholder="Operator notes (visible in audit log)" rows={3} className="bg-zinc-900 border-zinc-700" data-testid="reason-notes"/>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeModal} data-testid="modal-cancel">Cancel</Button>
            <Button onClick={submit} disabled={submitting || !reasonKey} className={modal?.mode === "ground" ? "bg-red-500 text-white hover:bg-red-600 font-bold" : "bg-emerald-500 text-white hover:bg-emerald-600 font-bold"} data-testid="modal-submit">
              {submitting ? "Saving…" : modal?.mode === "ground" ? "Confirm Ground" : "Confirm Restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
